import { SortableTh } from './SortableTh';
import { columnInfo, type ColumnInfoScope } from './columnInfo';
import type { SortDir } from '../useSortableRows';

/** The slice of a useSortableRows result the header cells need. */
interface ISortControls<K extends string> {
  sortKey: K;
  sortDir: SortDir;
  toggleSort: (key: K) => void;
}

/**
 * Header-cell renderer bound to one panel's sort state and columnInfo scope —
 * the shared form of the `th(columnKey, labelKey)` helper every breakdown
 * panel used to hand-roll. Lives apart from SortableTh.tsx so that file keeps
 * exporting only components (react-refresh). Usage:
 *
 *   const sort = useSortableRows(rows, { key: 'actualHours', dir: 'desc' });
 *   const th = makeSortableTh('subcontractor', sort, t);
 *   …
 *   {th('name', 'productivity.subcontractor.columns.subcontractor')}
 */
export function makeSortableTh<K extends string>(
  scope: ColumnInfoScope,
  sort: ISortControls<K>,
  t: (key: string) => string,
) {
  return function th(columnKey: K, labelKey: string) {
    return (
      <SortableTh
        label={t(labelKey)}
        columnKey={columnKey}
        sortKey={sort.sortKey}
        sortDir={sort.sortDir}
        onSort={sort.toggleSort}
        info={columnInfo(scope, columnKey, t)}
      />
    );
  };
}
